import { useEffect, useRef } from 'react';
import axiosInstance from '../api';
import API_ENDPOINTS from '../api/endpoints';
import useCountryStore from '../../Store/countryStore';
import useCurrencyStore, { DEFAULT_USD } from '../../Store/currencyStore';

const CURRENCY_STORAGE_KEY = 'selectedCurrency';

/**
 * Loads available currencies for the header dropdown and syncs
 * display currency whenever the selected country changes.
 * On refresh, keeps a previously chosen currency until the country changes.
 */
const useCurrency = () => {
    const selectedCountry = useCountryStore((state) => state.selectedCountry);
    const setAvailableCurrencies = useCurrencyStore((state) => state.setAvailableCurrencies);
    const setSelectedCurrency = useCurrencyStore((state) => state.setSelectedCurrency);
    const resetToUsd = useCurrencyStore((state) => state.resetToUsd);
    const lastCountryCodeRef = useRef(undefined);

    // Load all currencies once for the dropdown
    useEffect(() => {
        let cancelled = false;

        const loadCurrencies = async () => {
            try {
                const response = await axiosInstance.get(API_ENDPOINTS.UTILITIES.GET_CURRENCIES);
                const data = response?.data;
                const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
                if (!cancelled) {
                    setAvailableCurrencies(list);
                }
            } catch (error) {
                console.error('Error loading currencies:', error);
                if (!cancelled) {
                    setAvailableCurrencies([DEFAULT_USD]);
                }
            }
        };

        loadCurrencies();
        return () => {
            cancelled = true;
        };
    }, [setAvailableCurrencies]);

    // Sync currency when country changes
    useEffect(() => {
        const countryCode = selectedCountry?.code;
        if (!countryCode) return;

        let cancelled = false;

        const applyCurrencyForCountry = async () => {
            try {
                const response = await axiosInstance.get(API_ENDPOINTS.UTILITIES.GET_CURRENCY, {
                    params: { countryCode },
                });
                const data = response?.data;
                const currency = (data?.currencyCode || data?.CurrencyCode) ? data : data?.data;

                if (cancelled) return;

                if (currency) {
                    setSelectedCurrency(currency);
                } else {
                    resetToUsd();
                }
            } catch (error) {
                // 404 or any failure → stay with USD
                console.error('Error fetching currency for country:', error);
                if (!cancelled) {
                    resetToUsd();
                }
            }
        };

        // First time we see a country this session
        if (lastCountryCodeRef.current === undefined) {
            lastCountryCodeRef.current = countryCode;
            // Keep sessionStorage choice on refresh; fetch only on first visit
            const hasStoredCurrency = !!sessionStorage.getItem(CURRENCY_STORAGE_KEY);
            if (!hasStoredCurrency) {
                applyCurrencyForCountry();
            }
            return () => {
                cancelled = true;
            };
        }

        // Same country — no-op
        if (lastCountryCodeRef.current === countryCode) {
            return () => {
                cancelled = true;
            };
        }

        // Country changed — sync currency to new location
        lastCountryCodeRef.current = countryCode;
        applyCurrencyForCountry();

        return () => {
            cancelled = true;
        };
    }, [selectedCountry?.code, setSelectedCurrency, resetToUsd]);
};

export default useCurrency;
